import {
  Alert,
  Input,
  Modal,
  message,
} from '@duxton-web/ui';
import i18n from 'i18n-js';
import _ from 'lodash';
import {
  cpApprove,
  CP_AUDIENCE_ALL_SCOPE_KEY,
  CP_AUDIENCE_REGIONAL_SCOPE_KEY,
  cpCreateAudience,
  cpGetAudienceTableInfo,
  cpGetCountries,
  cpListAudiences,
  cpRecall,
  cpReject,
  cpUpdateAudience,
} from 'omega-rtc/actions/ucd';
import {
  AudienceColumnOption,
  AudienceConfigApi,
  AudienceFormValue,
  AudienceTableInfoState,
  AudienceValidationErrors,
  AudienceSourceType,
} from 'omega-rtc/types/ts/content-config';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';

import { useHolosPortalFeatureFlag } from 'omega-rtc/utils/portal-config';

import {
  STATUS_PENDING,
  audienceFromApi,
  audienceToApiBody,
  countryFromApi,
  localeSorter,
} from './adapters';
import { AudienceDetail, AudienceList } from './AudienceView';
import {
  EMPTY_CONTENT_CONFIG_PERMISSIONS,
  canRecallPendingChange,
  canReviewPendingChange,
  canSubmitAudienceForApproval,
} from './permissions';
import useContentConfigWrite from './useContentConfigWrite';

const DRAFT_ROW_KEY = '__audience_draft__';
const REGIONAL_SCOPE = 'regional';
const REGIONAL_ROW_KEY = CP_AUDIENCE_REGIONAL_SCOPE_KEY;
const EMPTY_ARRAY: any[] = [];
const EMPTY_AUDIENCES_BY_SCOPE: AudiencesByScope = {};
const EMPTY_TABLE_INFO_BY_SOURCE: Record<string, AudienceTableInfoState> = {};
const EMPTY_REQUEST: RequestState = {};
const audienceTableSourcePattern =
  /^[A-Za-z_][A-Za-z0-9_]*\.[A-Za-z_][A-Za-z0-9_]*$/;

type AudienceRecord = ReturnType<typeof audienceFromApi>;
type AudienceFormCandidate = Omit<AudienceFormValue, 'deeplink' | 'isDraft'> & {
  deeplink?: AudienceFormValue['deeplink'];
  isDraft?: boolean;
};

interface AudiencePair {
  active?: AudienceRecord;
  country: string;
  key: string;
  pending?: AudienceRecord;
  rowKey: string;
}

type AudiencesByScope = Record<string, {
  active?: AudienceConfigApi[];
  pending?: AudienceConfigApi[];
}>;

interface RequestState {
  isFailure?: boolean;
  isPending?: boolean;
}

const audienceText = (key, options = {}) =>
  i18n.t(`ucd_content_config.audiences.${key}`, options);

export const createAudienceDraft = (): AudienceFormValue => ({
  country: '',
  deeplink: {
    names: [],
    deepLinkTemplate: '',
  },
  description: '',
  isDraft: true,
  name: '',
  personaVariables: [],
  source: '',
  type: undefined,
});

const editorFromAudience = (audience: AudienceRecord): AudienceFormValue => ({
  country: audience.country || REGIONAL_SCOPE,
  deeplink: {
    names: [...((audience.deeplink && audience.deeplink.names) || [])],
    deepLinkTemplate: (audience.deeplink && audience.deeplink.deepLinkTemplate) || '',
  },
  description: audience.description,
  isDraft: false,
  name: audience.name,
  personaVariables: (audience.personaVariables || []).map((variable) => ({
    ...variable,
  })),
  source: audience.source,
  type: audience.type,
});

const normalizedBody = (audience: AudienceFormCandidate) => {
  const body = audienceToApiBody(audience);
  return {
    ...body,
    ...(body.deeplink ? {
      deeplink: {
        ...body.deeplink,
        names: [...body.deeplink.names].sort(),
      },
    } : {}),
    persona_variables: [...(body.persona_variables || [])]
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
};

const sameBody = (left: AudienceFormCandidate, right: AudienceFormCandidate) =>
  JSON.stringify(normalizedBody(left)) === JSON.stringify(normalizedBody(right));

const escapeRegExp = (value: string) =>
  String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const templateVariablePattern = /\{\{[ \t]*([^{}\s]+)[ \t]*\}\}/g;

const templateVariableNames = (template: string): string[] => {
  const names: string[] = [];
  let match = templateVariablePattern.exec(template);
  while (match) {
    names.push(match[1]);
    match = templateVariablePattern.exec(template);
  }
  return names;
};

const hasInvalidTemplateVariableSyntax = (template: string): boolean =>
  /[{}]/.test(template.replace(templateVariablePattern, ''));

export const isValidAudienceTableSource = (source: string): boolean =>
  audienceTableSourcePattern.test(source);

export const mergeAudienceConfigs = (
  activeRaw: AudienceConfigApi[],
  pendingRaw: AudienceConfigApi[],
): Record<string, AudiencePair> => {
  const result: Record<string, AudiencePair> = {};
  activeRaw.map(audienceFromApi).forEach((audience) => {
    const rowKey = `${audience.country || '__regional__'}:${audience.key}`;
    result[rowKey] = {
      ...(result[rowKey] || {}),
      active: audience,
      country: audience.country,
      key: audience.key,
      rowKey,
    };
  });
  pendingRaw.map(audienceFromApi).forEach((audience) => {
    const rowKey = `${audience.country || '__regional__'}:${audience.key}`;
    result[rowKey] = {
      ...(result[rowKey] || {}),
      country: audience.country,
      key: audience.key,
      pending: audience,
      rowKey,
    };
  });
  return result;
};

export const getSelectableColumnOptions = (
  resolvedColumns: Set<string>,
): AudienceColumnOption[] =>
  Array.from(resolvedColumns)
    .filter((column) => column !== 'user_id')
    .map((column) => ({ key: column, desc: column }));

export const validateAudienceForm = (
  candidate: AudienceFormCandidate,
  {
    resolvedColumns,
    tableResolved,
  }: {
    resolvedColumns: Set<string>;
    tableResolved: boolean;
  },
): AudienceValidationErrors => {
  const errors: AudienceValidationErrors = {};
  if (!candidate.country) errors.country = audienceText('validation.scope');
  if (!candidate.type) errors.type = audienceText('validation.source_type');
  if (!candidate.name.trim()) errors.name = audienceText('validation.name');
  if (!candidate.description.trim()) {
    errors.description = audienceText('validation.description');
  }
  if (!candidate.source.trim()) {
    errors.source = candidate.type === AudienceSourceType.Segment
      ? audienceText('validation.segment_source')
      : audienceText('validation.source');
  }

  if (candidate.type === AudienceSourceType.Table) {
    if (!isValidAudienceTableSource(candidate.source)) {
      errors.source = audienceText('validation.table_source');
    } else if (!tableResolved) {
      errors.source = audienceText('validation.load_columns');
    }

    if (!candidate.personaVariables.length) {
      errors.personaVariables = audienceText('validation.persona_variables');
    } else {
      const personaNames = candidate.personaVariables.map((variable) => variable.name);
      const invalidName = candidate.personaVariables.find((variable) =>
        variable.name === 'user_id'
        || !resolvedColumns.has(variable.name))
        || new Set(personaNames).size !== personaNames.length;
      if (invalidName) {
        errors.personaVariables = audienceText('validation.invalid_persona_variable');
      }
      const missingDescriptions = candidate.personaVariables.reduce<
        Record<string, string | undefined>
      >((result, variable) => {
        if (!String(variable.description || '').trim()) {
          result[variable.name] = audienceText(
            'validation.persona_variable_description',
          );
        }
        return result;
      }, {});
      if (Object.keys(missingDescriptions).length) {
        errors.personaVariableDescriptions = missingDescriptions;
      }
    }

    const deeplink = candidate.deeplink || {
      names: [],
      deepLinkTemplate: '',
    };
    const deeplinkNames = deeplink.names || [];
    const deepLinkTemplate = String(deeplink.deepLinkTemplate || '');
    const uniqueDeeplinkNames = new Set(deeplinkNames);
    const invalidDeeplinkNames = deeplinkNames.some((name) =>
      name === 'user_id' || !resolvedColumns.has(name))
      || uniqueDeeplinkNames.size !== deeplinkNames.length;
    if (invalidDeeplinkNames) {
      errors.deeplinkNames = audienceText('validation.invalid_deeplink_variable');
    } else if (!deeplinkNames.length) {
      if (deepLinkTemplate.trim()) {
        errors.deeplinkNames = audienceText('validation.deeplink_variables');
      }
    } else if (!deepLinkTemplate.trim()) {
      errors.deepLinkTemplate = audienceText('validation.deep_link_template');
    } else if (hasInvalidTemplateVariableSyntax(deepLinkTemplate)) {
      errors.deepLinkTemplate = audienceText(
        'validation.deep_link_invalid_reference',
      );
    } else {
      const missingReferences = deeplinkNames.filter((name) => {
        const reference = new RegExp(
          `\\{\\{[ \\t]*${escapeRegExp(name)}[ \\t]*\\}\\}`,
        );
        return !reference.test(deepLinkTemplate);
      });
      if (missingReferences.length) {
        errors.deepLinkTemplate = audienceText('validation.deep_link_references', {
          references: missingReferences.map((name) => `{{${name}}}`).join(', '),
        });
      } else {
        const selectedNames = new Set(deeplinkNames);
        const unknownReference = templateVariableNames(deepLinkTemplate)
          .find((name) => !selectedNames.has(name));
        if (unknownReference) {
          errors.deepLinkTemplate = audienceText(
            'validation.deep_link_unknown_reference',
            { reference: `{{${unknownReference}}}` },
          );
        }
      }
    }
  }
  return errors;
};

const AudiencePage = ({
  contentConfigPermissions = EMPTY_CONTENT_CONFIG_PERMISSIONS,
  navTick,
}) => {
  const dispatch = useDispatch();
  const userEmail: string = useSelector((state) =>
    _.get(state, 'app.user.email', ''));
  const cpCountries: any[] = useSelector((state) =>
    _.get(state, 'ucd.cpCountries', EMPTY_ARRAY));
  const audiencesByScope: AudiencesByScope = useSelector((state) =>
    _.get(state, 'ucd.cpAudiencesByScope', EMPTY_AUDIENCES_BY_SCOPE));
  const tableInfoBySource: Record<string, AudienceTableInfoState> = useSelector(
    (state) => _.get(
      state,
      'ucd.cpAudienceTableInfoBySource',
      EMPTY_TABLE_INFO_BY_SOURCE,
    ),
  );
  const activeListRequest: RequestState = useSelector((state) =>
    _.get(state, 'requests.cpListAudiencesRequest', EMPTY_REQUEST));
  const pendingListRequest: RequestState = useSelector((state) =>
    _.get(state, 'requests.cpListAudiencesPendingRequest', EMPTY_REQUEST));
  const createRequest: RequestState = useSelector((state) =>
    _.get(state, 'requests.cpCreateAudienceRequest', EMPTY_REQUEST));
  const updateRequest: RequestState = useSelector((state) =>
    _.get(state, 'requests.cpUpdateAudienceRequest', EMPTY_REQUEST));
  const tableSourceEnabled = useHolosPortalFeatureFlag(
    'enabledContentConfigAudienceTableSource',
  );

  const countries = useMemo(
    () => cpCountries.map(countryFromApi).sort(localeSorter('name')),
    [cpCountries],
  );
  useEffect(() => {
    dispatch(cpGetCountries());
  }, [dispatch]);

  const [selectedRowKey, setSelectedRowKey] = useState('');
  const [deepLink, setDeepLink] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      country: params.get('audienceCountry') || '',
      key: params.get('audienceKey') || '',
    };
  });
  const [form, setForm] = useState<AudienceFormValue | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [attemptedSource, setAttemptedSource] = useState('');
  const [validationErrors, setValidationErrors] =
    useState<AudienceValidationErrors>({});
  const [listTab, setListTab] = useState('all');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const listCountry = scopeFilter === 'all'
    ? undefined
    : scopeFilter === REGIONAL_SCOPE
      ? ''
      : scopeFilter;
  const scopeKey = scopeFilter === 'all'
    ? CP_AUDIENCE_ALL_SCOPE_KEY
    : scopeFilter === REGIONAL_SCOPE
      ? REGIONAL_ROW_KEY
      : scopeFilter;
  const activeRaw = _.get(audiencesByScope, [scopeKey, 'active'], EMPTY_ARRAY);
  const pendingRaw = _.get(audiencesByScope, [scopeKey, 'pending'], EMPTY_ARRAY);
  const merged = useMemo(
    () => mergeAudienceConfigs(activeRaw, pendingRaw),
    [activeRaw, pendingRaw],
  );
  const listPending = Boolean(activeListRequest.isPending)
    || Boolean(pendingListRequest.isPending);
  const refetchAudiences = useCallback(() => {
    dispatch(cpListAudiences(listCountry));
    dispatch(cpListAudiences(listCountry, STATUS_PENDING));
  }, [dispatch, listCountry]);
  useEffect(() => {
    refetchAudiences();
  }, [refetchAudiences]);
  const clearDeepLink = useCallback(
    () => setDeepLink({ country: '', key: '' }),
    [],
  );

  useEffect(() => {
    setSelectedRowKey('');
    setForm(null);
    setIsEditing(false);
    setShowPending(false);
    setValidationErrors({});
  }, [navTick]);

  const sawListLoad = useRef(false);
  useEffect(() => {
    if (!deepLink.key) return;
    const countryKey = !deepLink.country || deepLink.country === REGIONAL_SCOPE
      ? REGIONAL_ROW_KEY
      : deepLink.country;
    const rowKey = `${countryKey}:${deepLink.key}`;
    if (merged[rowKey]) {
      const pair = merged[rowKey];
      const editable = Boolean(
        pair.active
        && !pair.pending
        && contentConfigPermissions.canUpdate,
      );
      setSelectedRowKey(rowKey);
      setForm(editable ? editorFromAudience(pair.active) : null);
      setIsEditing(editable);
      if (editable && pair.active.type === AudienceSourceType.Table) {
        const source = pair.active.source.trim();
        setAttemptedSource(source);
        dispatch(cpGetAudienceTableInfo(source));
      }
      clearDeepLink();
      return;
    }
    if (listPending) sawListLoad.current = true;
    else if (sawListLoad.current) clearDeepLink();
  }, [
    clearDeepLink,
    contentConfigPermissions.canUpdate,
    deepLink.country,
    deepLink.key,
    dispatch,
    listPending,
    merged,
  ]);

  const [locationTick, setLocationTick] = useState(0);
  useEffect(() => {
    const unlisten = browserHistory.listen(
      () => setLocationTick((tick) => tick + 1),
    );
    return unlisten;
  }, []);

  useEffect(() => {
    if (deepLink.key) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') !== 'content-config') return;
    const before = params.toString();
    const pair = selectedRowKey && selectedRowKey !== DRAFT_ROW_KEY
      ? merged[selectedRowKey]
      : null;
    const audience = pair && (pair.active || pair.pending);
    if (audience) {
      params.set('audienceKey', audience.key);
      if (audience.country) params.set('audienceCountry', audience.country);
      else params.delete('audienceCountry');
    } else {
      params.delete('audienceKey');
      params.delete('audienceCountry');
    }
    const after = params.toString();
    if (after !== before) {
      browserHistory.replace(`${window.location.pathname}?${after}`);
    }
  }, [deepLink.key, locationTick, merged, selectedRowKey]);

  useEffect(() => () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') !== 'content-config') return;
    if (!params.has('audienceKey') && !params.has('audienceCountry')) return;
    params.delete('audienceKey');
    params.delete('audienceCountry');
    const query = params.toString();
    browserHistory.replace(
      `${window.location.pathname}${query ? `?${query}` : ''}`,
    );
  }, []);

  const selectedPair = merged[selectedRowKey];
  const active = selectedPair && selectedPair.active;
  const pending = selectedPair && selectedPair.pending;
  const isDraft = selectedRowKey === DRAFT_ROW_KEY;
  const record = isDraft
    ? form
    : selectedPair && (
      showPending && pending
        ? pending
        : active || pending
    );

  const tableInfo = attemptedSource
    ? tableInfoBySource[attemptedSource]
    : null;
  const tableResolved = Boolean(
    form
    && form.type === AudienceSourceType.Table
    && attemptedSource
    && attemptedSource === form.source
    && tableInfo
    && !tableInfo.isLoading
    && !tableInfo.error
    && Array.isArray(tableInfo.columns),
  );
  const resolvedColumns = useMemo<Set<string>>(
    () => new Set(
      tableResolved
        ? (tableInfo.columns || []).filter((column) => column !== 'user_id')
        : [],
    ),
    [tableInfo, tableResolved],
  );
  const selectableColumnOptions = useMemo(
    () => getSelectableColumnOptions(resolvedColumns),
    [resolvedColumns],
  );

  const scopeLabel = useCallback((country) => {
    if (!country) return audienceText('scope.regional');
    return (countries.find((item) => item.id === country) || {}).name || country;
  }, [countries]);

  const rows = useMemo(() => Object.values(merged).map((pair: any) => {
    const current = pair.active || pair.pending;
    return {
      name: current.name,
      rowKey: pair.rowKey,
      scopeLabel: scopeLabel(current.country),
      source: current.source,
      status: pair.pending ? 'pending' : 'active',
      type: current.type,
      updatedAt: current.updatedAt,
      updatedBy: current.updatedBy || current.createdBy,
      variableCount: (current.personaVariables || []).length,
      country: current.country,
    };
  }), [merged, scopeLabel]);

  const visibleRows = useMemo(() => rows.filter((row) => {
    if (listTab === 'pending' && row.status !== 'pending') return false;
    if (scopeFilter === REGIONAL_SCOPE && row.country) return false;
    if (sourceFilter !== 'all' && row.type !== sourceFilter) return false;
    return true;
  }), [listTab, rows, scopeFilter, sourceFilter]);

  const listError = Boolean(activeListRequest.isFailure)
    || Boolean(pendingListRequest.isFailure);
  const writeError = isDraft
    ? Boolean(createRequest.isFailure)
    : Boolean(updateRequest.isFailure);

  const resetToList = useCallback(() => {
    setSelectedRowKey('');
    setForm(null);
    setIsEditing(false);
    setShowPending(false);
    setAttemptedSource('');
    setValidationErrors({});
  }, []);

  const onWriteSuccess = useCallback(() => {
    resetToList();
    refetchAudiences();
  }, [refetchAudiences, resetToList]);
  const onWriteFailure = useCallback(() => {
    refetchAudiences();
  }, [refetchAudiences]);
  const { busy, runWrite } = useContentConfigWrite({
    onFailure: onWriteFailure,
    onSuccess: onWriteSuccess,
  });

  const notifyForbidden = () => message.error(audienceText('permissions.forbidden'));

  const validate = useCallback(
    (candidate) => validateAudienceForm(candidate, {
      resolvedColumns,
      tableResolved,
    }),
    [resolvedColumns, tableResolved],
  );

  const focusFirstError = (errors) => {
    const firstKey = Object.keys(errors)[0];
    if (!firstKey) return;
    window.setTimeout(() => {
      let selector = `[data-audience-field="${firstKey}"]`;
      if (firstKey === 'personaVariables') selector = '#audience-persona-variables';
      if (firstKey === 'personaVariableDescriptions') {
        selector = '[data-audience-field="personaVariableDescriptions"]';
      }
      if (firstKey === 'deeplinkNames') selector = '#audience-deeplink-variables';
      if (firstKey === 'deepLinkTemplate') selector = '#audience-deep-link-template';
      const root = document.querySelector(selector) as HTMLElement;
      const field = root && (
        root.matches('input, textarea, [role="combobox"], iframe')
          ? root
          : root.querySelector('input, textarea, [role="combobox"], iframe')
      ) as HTMLElement;
      if (field && typeof field.focus === 'function') field.focus();
    }, 0);
  };

  const onCreate = () => {
    if (!contentConfigPermissions.canCreate) {
      notifyForbidden();
      return;
    }
    setSelectedRowKey(DRAFT_ROW_KEY);
    setForm(createAudienceDraft());
    setIsEditing(true);
    setShowPending(false);
    setAttemptedSource('');
    setValidationErrors({});
  };

  const onOpenDetail = (rowKey) => {
    const pair = merged[rowKey];
    const editable = Boolean(
      pair
      && pair.active
      && !pair.pending
      && contentConfigPermissions.canUpdate,
    );
    setSelectedRowKey(rowKey);
    setForm(editable ? editorFromAudience(pair.active) : null);
    setIsEditing(editable);
    setShowPending(false);
    setAttemptedSource('');
    setValidationErrors({});
    if (editable && pair.active.type === AudienceSourceType.Table) {
      const source = pair.active.source;
      if (isValidAudienceTableSource(source)) {
        setAttemptedSource(source);
        dispatch(cpGetAudienceTableInfo(source));
      }
    }
  };

  const onChange = (
    field: 'country' | 'description' | 'name' | 'source' | 'type',
    value: string | undefined,
  ) => {
    const invalidatesTableSelections = field === 'type' || field === 'source';
    setValidationErrors((current) => ({
      ...current,
      [field]: undefined,
      ...(invalidatesTableSelections ? {
        deepLinkTemplate: undefined,
        deeplinkNames: undefined,
        personaVariableDescriptions: undefined,
        personaVariables: undefined,
      } : {}),
    }));
    setForm((current) => {
      if (!current) return current;
      if (field === 'type') {
        setAttemptedSource('');
        return {
          ...current,
          deeplink: {
            names: [],
            deepLinkTemplate: '',
          },
          personaVariables: [],
          source: '',
          type: value as AudienceSourceType | undefined,
        };
      }
      if (field === 'source') {
        setAttemptedSource('');
        return {
          ...current,
          deeplink: {
            names: [],
            deepLinkTemplate: '',
          },
          personaVariables: [],
          source: value,
        };
      }
      return { ...current, [field]: value };
    });
  };

  const onResolveTable = () => {
    if (!form) return;
    const source = form.source;
    if (!isValidAudienceTableSource(source)) {
      const nextErrors = {
        ...validationErrors,
        source: audienceText('validation.table_source'),
      };
      setValidationErrors(nextErrors);
      focusFirstError(nextErrors);
      return;
    }
    setValidationErrors((current) => ({ ...current, source: undefined }));
    setAttemptedSource(source);
    dispatch(cpGetAudienceTableInfo(source));
  };

  const onVariableKeysChange = (keys: string[]) => {
    const columnByKey = new Map(
      selectableColumnOptions.map((column) => [column.key, column]),
    );
    setForm((current) => {
      if (!current) return current;
      const currentByKey = new Map(
        current.personaVariables.map((variable) => [variable.name, variable]),
      );
      return {
        ...current,
        personaVariables: keys
          .filter((key) => key !== 'user_id' && columnByKey.has(key))
          .map((key) => currentByKey.get(key) || {
            description: '',
            name: key,
          }),
      };
    });
    setValidationErrors((current) => ({
      ...current,
      personaVariableDescriptions: undefined,
      personaVariables: undefined,
    }));
  };

  const onVariableDescriptionChange = (name: string, description: string) => {
    setForm((current) => current && ({
      ...current,
      personaVariables: current.personaVariables.map((variable) =>
        variable.name === name ? { ...variable, description } : variable),
    }));
    setValidationErrors((current) => ({
      ...current,
      personaVariableDescriptions: {
        ...(current.personaVariableDescriptions || {}),
        [name]: undefined,
      },
    }));
  };

  const onDeeplinkKeysChange = (names: string[]) => {
    const selectableNames = new Set(
      selectableColumnOptions.map((column) => column.key),
    );
    const nextNames = names.filter((name) =>
      name !== 'user_id' && selectableNames.has(name));
    setForm((current) => current && ({
      ...current,
      deeplink: {
        names: nextNames,
        deepLinkTemplate: nextNames.length
          ? ((current.deeplink && current.deeplink.deepLinkTemplate) || '')
          : '',
      },
    }));
    setValidationErrors((current) => ({
      ...current,
      deeplinkNames: undefined,
      deepLinkTemplate: undefined,
    }));
  };

  const onDeeplinkTemplateChange = (deepLinkTemplate: string) => {
    setForm((current) => current && ({
      ...current,
      deeplink: {
        ...current.deeplink,
        deepLinkTemplate,
      },
    }));
    setValidationErrors((current) => ({
      ...current,
      deepLinkTemplate: undefined,
    }));
  };

  const onSubmit = () => {
    if (!form) return;
    const errors = validate(form);
    setValidationErrors(errors);
    if (Object.keys(errors).length) {
      focusFirstError(errors);
      return;
    }

    const hasEdits = Boolean(active) && !sameBody(form, active);
    const canSubmit = canSubmitAudienceForApproval(
      contentConfigPermissions,
      {
        isDraft,
        isValid: true,
        hasActive: Boolean(active),
        hasEdits,
        hasPending: Boolean(pending),
      },
    );
    if (!canSubmit) {
      if (!isDraft && !hasEdits) {
        message.error(audienceText('validation.no_changes'));
      } else {
        notifyForbidden();
      }
      return;
    }

    const body = audienceToApiBody(form);
    if (isDraft) {
      const country = form.country === REGIONAL_SCOPE ? '' : form.country;
      runWrite(
        cpCreateAudience(country, body, userEmail),
        'createAudience',
      );
      return;
    }
    runWrite(
      cpUpdateAudience(active.id, body, userEmail),
      'updateAudience',
    );
  };

  const isMine = Boolean(
    pending
    && (pending.updatedBy || pending.createdBy) === userEmail,
  );
  const canRecall = Boolean(pending)
    && canRecallPendingChange(contentConfigPermissions, isMine);
  const canReview = Boolean(pending)
    && canReviewPendingChange(contentConfigPermissions, isMine);
  const canEdit = Boolean(active)
    && !pending
    && contentConfigPermissions.canUpdate;
  const hasChanges = isDraft || Boolean(
    canEdit
    && form
    && !sameBody(active, form),
  );

  const onApprove = () => {
    if (!pending || !canReview) {
      notifyForbidden();
      return;
    }
    runWrite(cpApprove(pending.id, userEmail), 'approve');
  };
  const onReject = (comment) => {
    if (!pending || !canReview || !comment.trim()) {
      notifyForbidden();
      return;
    }
    runWrite(cpReject(pending.id, userEmail, comment.trim()), 'reject');
  };
  const onRequestReject = () => {
    if (!pending || !canReview) {
      notifyForbidden();
      return;
    }
    let comment = '';
    Modal.confirm({
      title: audienceText('messages.reject_title'),
      content: (
        <Input.TextArea
          placeholder={audienceText('placeholders.rejection_comment')}
          onChange={(event) => { comment = event.target.value; }}
        />
      ),
      okText: audienceText('actions.reject'),
      okType: 'danger',
      onOk: () => {
        if (!comment.trim()) {
          message.error(audienceText('validation.rejection_comment'));
          return Promise.reject();
        }
        onReject(comment);
        return Promise.resolve();
      },
    });
  };
  const onRecall = () => {
    if (!pending || !canRecall) {
      notifyForbidden();
      return;
    }
    runWrite(cpRecall(pending.id, userEmail), 'recall');
  };

  return (
    <div className="cc-audiences-page">
      {selectedRowKey ? (
        <React.Fragment>
          {writeError && isEditing && (
            <Alert
              type="error"
              showIcon
              role="alert"
              className="cc-action-bar"
              message={audienceText('errors.write')}
            />
          )}
          <AudienceDetail
            active={active}
            busy={busy}
            canRecall={canRecall}
            canReview={canReview}
            countries={countries}
            errors={validationErrors}
            form={form}
            hasChanges={hasChanges}
            isDraft={isDraft}
            isEditing={isEditing}
            onApprove={onApprove}
            onCancelEdit={() => {
              if (isDraft) resetToList();
              else {
                setForm(editorFromAudience(active));
                setIsEditing(true);
                setAttemptedSource('');
                setValidationErrors({});
              }
            }}
            onChange={onChange}
            onRecall={onRecall}
            onReject={onReject}
            onRequestReject={onRequestReject}
            onResolveTable={onResolveTable}
            onSubmit={onSubmit}
            onDeeplinkKeysChange={onDeeplinkKeysChange}
            onDeeplinkTemplateChange={onDeeplinkTemplateChange}
            onVariableDescriptionChange={onVariableDescriptionChange}
            onVariableKeysChange={onVariableKeysChange}
            pending={pending}
            record={record}
            selectableColumnOptions={selectableColumnOptions}
            setShowPending={setShowPending}
            showPending={showPending}
            tableInfo={tableInfo}
            tableResolved={tableResolved}
            tableSourceEnabled={tableSourceEnabled}
          />
        </React.Fragment>
      ) : (
        <AudienceList
          canCreate={contentConfigPermissions.canCreate}
          countries={countries}
          listError={listError}
          listTab={listTab}
          loading={listPending && rows.length === 0}
          onCreate={onCreate}
          onOpenDetail={onOpenDetail}
          onRetry={refetchAudiences}
          rows={visibleRows}
          scopeFilter={scopeFilter}
          setListTab={setListTab}
          setScopeFilter={setScopeFilter}
          setSourceFilter={setSourceFilter}
          sourceFilter={sourceFilter}
        />
      )}
    </div>
  );
};

export default AudiencePage;
